import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '../../../lib/queryKeys'
import { createPickupOrder, getPickupOrders, updatePickupOrder, uploadPickupOrderBarcode } from '../pickupOrdersService'
import { PickupOrder, PickupOrderStatus } from '../types/PickupOrder'

interface Props {
  status?: string
}

export const usePickupOrders = ({ status }: Props = {}) => {
  const queryKey = [QUERY_KEYS.PICKUP_ORDERS, status].filter(Boolean) as string[]

  return useQuery({
    queryKey,
    queryFn: () => getPickupOrders({ status }),
  })
}

export const useCreatePickupOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createPickupOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS], exact: false })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PICKUP_ORDERS] })
    },
  })
}

export const useUpdatePickupOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PickupOrderStatus; oldStatus: PickupOrderStatus }) =>
      updatePickupOrder(id, { status }),
    onMutate: async ({ id, status, oldStatus }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS, oldStatus] })
      const previousOrders = queryClient.getQueryData<PickupOrder[]>([QUERY_KEYS.PICKUP_ORDERS, oldStatus])
      queryClient.setQueryData<PickupOrder[]>([QUERY_KEYS.PICKUP_ORDERS, oldStatus], old =>
        old?.map(order => (order.id === id ? { ...order, status } : order))
      )
      return { previousOrders }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousOrders) {
        queryClient.setQueryData([QUERY_KEYS.PICKUP_ORDERS], context.previousOrders)
      }
    },
  })
}

export const useUploadPickupOrderBarcode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, barcodeFile }: { id: string; barcodeFile: File }) => uploadPickupOrderBarcode(id, barcodeFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS], exact: false })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PICKUP_ORDERS] })
    },
  })
}
